import { Button } from "@cohesive/ui-components/ui/button";
import { ExternalLink, type LucideIcon } from "lucide-react";
import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ComponentType,
  type CSSProperties,
  type ReactNode,
} from "react";
import { CARD_W, placeCard, type Size } from "./place";
import type { Align, Key, Rect, Side, Tint } from "./types";

export interface CoachmarkProps {
  /** Changes with the card; remeasures and refocuses. */
  id: string;
  tint: Tint;
  icon: LucideIcon;
  /** The target's live rect. */
  rect: Rect | null;
  /** Regions the card keeps off; see placeCard. */
  clear?: Rect | readonly Rect[] | null;
  side: Side;
  align: Align;
  step: number;
  count: number;
  title: string;
  body: string;
  figure?: ComponentType;
  keys?: Key[];
  docs?: { href: string; label: string } | null;
  onNext: () => void;
  onSkip: () => void;
  onDone: () => void;
}

function Kbd({ children }: { children: ReactNode }) {
  return <kbd className="cm-kbd">{children}</kbd>;
}

/**
 * The card. It measures itself and places on the next frame (hidden until
 * it has a size, so it never flashes at the wrong spot), then takes focus:
 * Enter and Escape reach it by intent, a screen reader hears it, and a
 * control the viewer had focused keeps its own keys until they move. No
 * focus ring: the card is not a control.
 */
export function Coachmark({
  id,
  tint,
  icon: Icon,
  rect,
  clear = null,
  side,
  align,
  step,
  count,
  title,
  body,
  figure: Figure,
  keys,
  docs,
  onNext,
  onSkip,
  onDone,
}: CoachmarkProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState<Size | null>(null);
  const [vp, setVp] = useState<Size>(() => ({
    w: window.innerWidth,
    h: window.innerHeight,
  }));

  const hasRect = rect !== null;
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const read = () => {
      const r = el.getBoundingClientRect();
      setSize((s) =>
        s && Math.abs(s.w - r.width) < 0.5 && Math.abs(s.h - r.height) < 0.5
          ? s
          : { w: r.width, h: r.height },
      );
    };
    read();
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(read);
    ro.observe(el);
    return () => ro.disconnect();
    // The card mounts only once its target has a rect, so the observer binds
    // on that transition too, not only on a new card.
  }, [id, hasRect]);

  useEffect(() => {
    const onResize = () =>
      setVp({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const placed = Boolean(size && rect);
  useEffect(() => {
    if (placed) ref.current?.focus({ preventScroll: true });
  }, [id, placed]);

  if (!rect) return null;
  const pos = size ? placeCard(rect, side, align, size, vp, clear) : null;
  const last = step === count;
  const style = {
    width: CARD_W,
    left: pos?.left ?? 0,
    top: pos?.top ?? 0,
    "--cm-stem": pos ? `${pos.stem}px` : undefined,
    "--cm-at": pos ? `${pos.at}px` : undefined,
  } as CSSProperties;

  return (
    <div
      ref={ref}
      key={id}
      className="cm-card"
      role="dialog"
      aria-label={title}
      tabIndex={-1}
      data-tint={tint}
      data-side={pos?.side ?? side}
      data-measuring={pos ? undefined : ""}
      data-enter=""
      style={style}
    >
      <span className="cm-stem" aria-hidden="true" />
      {Figure && (
        <div className="cm-figure">
          <Figure />
        </div>
      )}
      <div className="cm-body">
        <h2 className="cm-title">
          <Icon className="cm-glyph" strokeWidth={1.75} aria-hidden="true" />
          <span>{title}</span>
        </h2>
        <p className="cm-text">{body}</p>
        {keys && (
          <div className="cm-keys">
            {keys.map((k, i) =>
              typeof k === "string" ? (
                <Kbd key={i}>{k}</Kbd>
              ) : (
                <span key={i} className="cm-keys">
                  <span className="cm-kbd-label">{k.label}</span>
                  {k.keys.map((kk) => (
                    <Kbd key={kk}>{kk}</Kbd>
                  ))}
                </span>
              ),
            )}
          </div>
        )}
        {docs && (
          <a
            className="cm-docs"
            href={docs.href}
            target="_blank"
            rel="noreferrer"
          >
            {docs.label}
            <ExternalLink aria-hidden="true" />
          </a>
        )}
      </div>
      <div className="cm-foot">
        {count > 1 && (
          <span className="cm-step">
            {step} of {count}
          </span>
        )}
        {/* The verbs ride the identity constants: the note is the same paper
            in both modes, so the ladder's primary (which inverts) would come
            up pale on it in the dark. */}
        <div className="cm-verbs">
          {!last && (
            <Button
              variant="ghost"
              size="sm"
              className="text-ink hover:bg-ink/10 hover:text-ink"
              onClick={onSkip}
            >
              Skip
            </Button>
          )}
          <Button
            variant="primary"
            size="sm"
            className="bg-ink text-paper hover:bg-ink/85"
            onClick={last ? onDone : onNext}
          >
            {last ? "Done" : "Next"}
          </Button>
        </div>
      </div>
    </div>
  );
}
